/**
 * Formati dei blocchi: nomi leggibili e icone, in un posto solo.
 *
 * Prima il nome veniva ricavato con formato.toUpperCase() in cinque punti, e
 * all'atleta compariva "A_TEMPO" con il trattino basso; la mappa delle icone
 * era copiata in quattro file. Aggiungendo un formato bastava dimenticare un
 * punto per avere etichette incoerenti.
 */

export const NOME_FORMATO = {
  classico: 'Classico',
  emom: 'EMOM',
  amrap: 'AMRAP',
  tabata: 'Tabata',
  circuit: 'Circuit',
  a_tempo: 'A tempo',
}

export const ICONA_FORMATO = {
  classico: '🏋️',
  emom: '⚡',
  amrap: '♾️',
  tabata: '🔥',
  circuit: '🔄',
  a_tempo: '⏱️',
}

/**
 * Colore di ogni formato: è l'identità visiva del blocco.
 *
 * Gli stessi colori esistevano già in index.css (.formato-classico e compagnia)
 * ma non li usava nessuno. Nella schermata di allenamento ogni blocco era una
 * card grigia uguale all'altra, su uno sfondo quasi identico — 1,08 di contrasto
 * fra #161820 e #0d0f14 — e l'atleta non distingueva dove finiva un blocco e
 * cominciava il successivo.
 *
 * 'testo' è una variabile CSS, così segue anche il tema chiaro; 'tinta' è il
 * velo di sfondo, translucido, che regge su entrambi i temi.
 */
export const COLORE_FORMATO = {
  classico: { testo: 'var(--accent)',  tinta: 'rgba(127,214,61,0.12)' },
  emom:     { testo: 'var(--green)',   tinta: 'rgba(74,222,128,0.12)' },
  amrap:    { testo: 'var(--accent2)', tinta: 'rgba(87,184,255,0.12)' },
  tabata:   { testo: 'var(--accent3)', tinta: 'rgba(255,120,87,0.12)' },
  circuit:  { testo: 'var(--purple)',  tinta: 'rgba(199,125,255,0.12)' },
  a_tempo:  { testo: 'var(--yellow)',  tinta: 'rgba(255,217,61,0.12)' },
}

export function coloreFormato(id) {
  return COLORE_FORMATO[id] || { testo: 'var(--muted)', tinta: 'rgba(255,255,255,0.06)' }
}

/** I parametri del blocco in parole, senza ripetere il nome del formato. */
export function parametriBlocco(blocco) {
  const imp = blocco?.impostazioni || {}
  const pezzi = []
  if (imp.durata) pezzi.push(`${imp.durata} min`)
  if (blocco?.formato === 'circuit') {
    // Nel circuito la pausa sta fra i GIRI, non fra gli esercizi: scriverla
    // come "40s lavoro / 60s pausa" faceva credere un minuto di stop ogni esercizio.
    if (imp.giri) pezzi.push(`${imp.giri} giri`)
    pezzi.push(`${lavoroDelBlocco(blocco)}s per esercizio`)
    if (imp.pausa) pezzi.push(`${imp.pausa}s tra i giri`)
  } else {
    // "1 giro" non è un'informazione: si scrive solo se i giri sono più d'uno.
    if (imp.giri > 1) pezzi.push(`${imp.giri} giri`)
    if (imp.lavoro) pezzi.push(`${imp.lavoro}s lavoro${imp.pausa ? ` / ${imp.pausa}s pausa` : ''}`)
    else if (blocco?.formato === 'classico' && imp.riposo) pezzi.push(`${imp.riposo}s di recupero`)
  }
  return pezzi.join(' · ')
}

/**
 * Chi decide quanti secondi dura un esercizio a tempo: il blocco o l'esercizio.
 *
 * Era la stessa domanda con tre risposte diverse nello stesso programma. Il
 * coach impostava un blocco "A tempo" da 40s di lavoro, poi dentro al blocco
 * il Plank mostrava un campo SECONDI con 30, e salvava: nel database finivano
 * entrambi i numeri. In esecuzione il cronometro usava i 40 del blocco e i 30
 * non li leggeva nessuno, ma il riepilogo del coach scriveva "30s". Un numero
 * che si può digitare ma che non succede mai è peggio di un campo assente.
 *
 * Dove la struttura del formato detta già il tempo, comanda il blocco e il
 * campo dell'esercizio non si mostra; dove non lo detta (Classico, AMRAP) il
 * tempo è dell'esercizio. Coach e atleta leggono questa stessa funzione, così
 * il numero mostrato nella creazione, quello mostrato nell'allenamento e
 * quello che il cronometro esegue non possono più divergere.
 */
/* L'EMOM NON sta in questa mappa, anche se ha una struttura a tempo: il minuto
 * è la CADENZA, non la durata del lavoro. "Plank 30s ogni minuto" vuol dire
 * trenta secondi di lavoro e trenta di recupero, non un minuto di plank
 * continuo. Averlo messo qui faceva eseguire 60s a qualunque esercizio a tempo
 * dentro un EMOM, e toglieva al coach perfino il campo per dire quanti secondi. */
export const LAVORO_DAL_BLOCCO = {
  a_tempo: imp => imp.lavoro || 40,
  tabata: imp => imp.lavoro || 20,
  circuit: imp => imp.lavoro || 40,
}

/** Secondi di lavoro imposti dal blocco, o null se li decide l'esercizio. */
export function lavoroDelBlocco(blocco) {
  const regola = LAVORO_DAL_BLOCCO[blocco?.formato]
  return regola ? regola(blocco?.impostazioni || {}) : null
}

/** Secondi da mostrare ED eseguire per un esercizio a tempo. */
export function secondiEsercizio(blocco, ex) {
  const dalBlocco = lavoroDelBlocco(blocco)
  if (dalBlocco != null) return dalBlocco
  return ex?.durata || 30
}

/**
 * Quante volte l'atleta esegue quell'esercizio dentro il blocco.
 *
 * Mancava del tutto nei formati a tempo. Un blocco "A tempo" da 40s con dentro
 * un Plank voleva dire: 40 secondi di plank e il blocco è finito. Non c'era modo
 * di dire "5 volte da 40 secondi con 20 di pausa", che è il caso normale.
 * Peggio: l'atleta non aveva nessun tetto, perché il tetto leggeva ex.serie, che
 * per un esercizio a tempo è sempre vuoto — poteva registrarne quante voleva.
 *
 * Nei formati a struttura è il blocco a dire quante volte (i giri, i round, i
 * minuti); nel Classico lo dice l'esercizio con le sue serie. L'AMRAP resta
 * senza numero, perché "più giri possibili" è la sua definizione.
 */
export function ripetizioniPreviste(blocco, ex) {
  const imp = blocco?.impostazioni || {}
  const lista = blocco?.esercizi || []
  // AMRAP: "più giri possibili" è la sua definizione, quindi nessun tetto, mai.
  // Prima leggeva ex.serie, che vale 3 per come nasce ogni esercizio a
  // ripetizioni e che in questo formato il coach non può nemmeno vedere: gli
  // atleti venivano fermati al terzo giro da un numero deciso da nessuno.
  if (blocco?.formato === 'amrap') return null
  if (blocco?.formato === 'a_tempo') return Math.max(1, imp.giri || 1)
  if (blocco?.formato === 'circuit') return Math.max(1, imp.giri || 3)
  if (blocco?.formato === 'tabata') return Math.max(1, imp.round || 8)
  if (blocco?.formato === 'emom') {
    // Gli esercizi ruotano un minuto ciascuno: con 10 minuti e 3 esercizi il
    // primo tocca 4 volte, gli altri 3.
    const minuti = Math.max(1, imp.durata || 10)
    const quanti = lista.length || 1
    const i = Math.max(0, lista.findIndex(e => e === ex || (e?.nome && e.nome === ex?.nome)))
    return Math.max(1, Math.floor((minuti - i - 1) / quanti) + 1)
  }
  const n = Number(ex?.serie)
  return n > 0 ? n : null
}

/** Perché quei secondi: spiegato al coach accanto al numero, invece di un campo
 *  modificabile che non avrebbe effetto. */
export function origineSecondi(formato) {
  if (formato === 'a_tempo') return 'secondi di lavoro del blocco'
  if (formato === 'tabata') return 'durata fissa del Tabata'
  if (formato === 'circuit') return 'secondi di lavoro del giro'
  return ''
}

/** Nome leggibile del formato; se sconosciuto ripiega sull'id senza trattini bassi. */
export function nomeFormato(id) {
  return NOME_FORMATO[id] || (id ? String(id).replace(/_/g, ' ') : '')
}

/** Valori predefiniti di un formato, scritti nel blocco quando lo si sceglie.
 *  Prima erano solo mostrati nei campi (value={... || 40}) ma mai salvati: se il
 *  coach non li toccava, il blocco finiva nel database senza quei valori e
 *  l'atleta leggeva "30s/undefineds". */
export const IMPOSTAZIONI_PREDEFINITE = {
  classico: { riposo: 90 },
  emom: { durata: 10 },
  amrap: { durata: 10 },
  // giri: quante volte si ripete la lista di esercizi del blocco. Senza, un
  // blocco con un solo esercizio durava un giro solo e si chiudeva lì.
  a_tempo: { lavoro: 40, pausa: 20, giri: 1 },
  // lavoro: il circuito eseguiva 40s hardcoded nel cronometro senza che il coach
  // avesse un campo per cambiarli. Ora è un'impostazione come le altre.
  circuit: { giri: 3, pausa: 60, lavoro: 40 },
  tabata: {},   // 8 round × 20s/10s, non configurabile
}

/**
 * Il carico di una serie: quello scritto dall'atleta, o l'ultimo usato.
 *
 * Questa funzione esiste per una ragione precisa. Il campo KG della sessione di
 * gruppo era non controllato, con defaultValue preso dall'ultimo allenamento,
 * mentre il salvataggio leggeva `log.kg || 0`, che esiste solo dopo un onChange.
 * Due fonti per lo stesso numero, e divergevano nel caso più comune di tutti:
 * l'atleta vedeva 37 nel campo, faceva la serie con 37, non toccava niente
 * perché era già giusto, e a database finiva 0. I progressi risultavano piatti
 * proprio per chi allenava con costanza lo stesso peso.
 *
 * Adesso il campo MOSTRA quello che questa funzione calcola e il salvataggio
 * SALVA quello che questa funzione calcola. Finché entrambi la chiamano, non
 * possono più dire cose diverse — non perché qualcuno si ricordi di tenerli
 * allineati, ma perché il numero è uno.
 *
 * `scritto` è la riga di stato dei campi (logData in gruppo,
 * sessPersonaleRisultati da solo); `ultimo` è l'ultimo record di
 * tracking_esercizio per quell'esercizio, o null se non se n'è mai fatto uno.
 *
 * Si usa ?? e non ||: uno zero digitato a mano è una scelta dell'atleta
 * (esercizio a corpo libero, elastico di aiuto) e non deve essere scambiato per
 * "campo vuoto, rimetti l'ultimo peso".
 */
export function caricoDi(scritto, ultimo) {
  const s = scritto || {}
  if (s.usaElastico) {
    return { peso: 0, elastico: s.elastico ?? ultimo?.elastico ?? null }
  }
  return { peso: s.kg ?? ultimo?.peso ?? 0, elastico: null }
}

/** Restituisce sempre una lista. In archivio alcuni esercizi avevano varianti/regressioni
 *  salvate come oggetto vuoto {}: espanderle con lo spread lanciava "{} is not iterable"
 *  e mandava in errore il pannello admin e il caricamento delle sessioni. */
export function comeLista(v) {
  return Array.isArray(v) ? v : []
}
